import { createContext, useContext, ReactNode, useEffect, useState, useCallback } from "react";
import { useApp } from "./AppContext";

/**
 * web search result type
 */
export interface SearchResult {
  title: string;
  description: string;
  url: string;
}

export interface WebContextType {
  loading: boolean;
  error: string | null;
  results: SearchResult[];

  // actions
  search: (query: string) => void;
  clearError: () => void;
}

const WebContext = createContext<WebContextType | undefined>(undefined);

export function useWeb() {
  const context = useContext(WebContext);
  if (!context) {
    throw new Error("useWeb must be used within a WebProvider");
  }
  return context;
}

export default function WebProvider({ children }: { children: ReactNode }) {
  const { sessionReady, onMessage, sendMessage } = useApp();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SearchResult[]>([]);

  // ===== DATA FETCHING =====
  const search = useCallback(
    (query: string) => {
      if (!query.trim()) {
        setResults([]);
        return;
      }

      if (!sessionReady) {
        console.warn("[Web] cannot search: session not ready");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        sendMessage("web", "search", { query });
      } catch (err) {
        console.error("[Web] failed to search:", err);
        setError("failed to search");
        setLoading(false);
      }
    },
    [sessionReady, sendMessage],
  );

  // ===== MESSAGE HANDLERS =====
  const handleWebMessage = useCallback((message: any) => {
    // only handle web service messages
    if (message.service !== "web") return;

    if (message.action === "search_results") {
      const resultsData = message.payload?.results || [];
      setResults(resultsData);
      setLoading(false);
      return;
    }

    if (message.action === "error") {
      const errorMessage = message.payload?.message || "unknown error";
      console.error("[Web] error:", errorMessage);
      setError(errorMessage);
      setLoading(false);
      // auto-clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
      return;
    }
  }, []);

  // clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ===== SUBSCRIPTIONS =====
  useEffect(() => {
    const unsub1 = onMessage("search_results", handleWebMessage);
    const unsub2 = onMessage("error", handleWebMessage);

    return () => {
      unsub1();
      unsub2();
    };
  }, [onMessage, handleWebMessage]);

  const value: WebContextType = {
    loading,
    error,
    results,

    search,
    clearError,
  };

  return <WebContext.Provider value={value}>{children}</WebContext.Provider>;
}
